/**
 * ==============================================================================
 * 15 YEAR ANNIVERSARY PAGE JAVASCRIPT
 * ==============================================================================
 *
 * This file handles all interactive functionality for the anniversary page:
 * - Hero parallax scrolling
 * - Timeline scroll animation and milestone visibility
 * - Technology showcase tabs
 * - YouTube video lazy loading
 * - Testimonials carousel
 *
 * Architecture: Modular functions with clear separation of concerns
 * Performance: Optimized with requestAnimationFrame and passive listeners
 * Accessibility: ARIA attributes and keyboard support
 */

"use strict";

/**
 * ==============================================================================
 * UTILITY FUNCTIONS
 * ==============================================================================
 */

/**
 * Throttle function calls using requestAnimationFrame
 * @param {Function} fn - Function to throttle
 * @returns {Function} - Throttled function
 */
function throttleRAF(fn) {
  let ticking = false;
  return function (...args) {
    if (!ticking) {
      requestAnimationFrame(() => {
        fn.apply(this, args);
        ticking = false;
      });
      ticking = true;
    }
  };
}

/**
 * Safe element selector with error handling
 * @param {string} selector - CSS selector
 * @param {Element} context - Context element (optional)
 * @returns {Element|null} - Found element or null
 */
function safeSelect(selector, context = document) {
  try {
    return context.querySelector(selector);
  } catch (error) {
    console.warn(`Invalid selector: ${selector}`, error);
    return null;
  }
}

/**
 * Safe multiple element selector
 * @param {string} selector - CSS selector
 * @param {Element} context - Context element (optional)
 * @returns {NodeList} - Found elements or empty NodeList
 */
function safeSelectAll(selector, context = document) {
  try {
    return context.querySelectorAll(selector);
  } catch (error) {
    console.warn(`Invalid selector: ${selector}`, error);
    return [];
  }
}

/**
 * ==============================================================================
 * HERO SECTION - Parallax Scrolling
 * ==============================================================================
 */

const HeroParallax = {
  init() {
    this.heroSection = safeSelect("#lp-hero-section");
    this.heroBackground = this.heroSection?.querySelector(
      ".lp-hero-background"
    );

    if (!this.heroBackground) {
      console.log("Hero parallax: Elements not found");
      return;
    }

    this.handleScroll = throttleRAF(this.updateParallax.bind(this));
    this.bindEvents();
    console.log("Hero parallax initialized");
  },

  updateParallax() {
    if (!this.heroBackground) return;

    const scrollY = window.pageYOffset;
    const parallaxOffset = Math.min(scrollY * 0.3, 100);

    this.heroBackground.style.setProperty(
      "--parallax-offset",
      `${parallaxOffset}px`
    );
    this.heroBackground.style.transform = `translate3d(0, ${parallaxOffset}px, 0)`;
  },

  bindEvents() {
    window.addEventListener("scroll", this.handleScroll, { passive: true });
  },

  destroy() {
    if (this.handleScroll) {
      window.removeEventListener("scroll", this.handleScroll);
    }
  },
};

/**
 * ==============================================================================
 * TIMELINE SECTION - Scroll Animation & Milestone Visibility
 * ==============================================================================
 */

const TimelineAnimation = {
  init() {
    this.section = safeSelect("#lp-tl-legacy-timeline");
    this.timeline = safeSelect("#lp-tl-timeline");
    this.lineFill = this.timeline?.querySelector(".lp-tl-line-fill");
    this.milestones = safeSelectAll(".lp-tl-milestone", this.section);

    if (!this.section || !this.timeline || !this.lineFill) {
      console.log("Timeline animation: Required elements not found");
      return;
    }

    this.handleScroll = throttleRAF(this.updateLine.bind(this));
    this.initMilestoneVisibility();
    this.bindEvents();
    this.updateLine(); // Initial update
    console.log("Timeline animation initialized");
  },

  updateLine() {
    if (!this.timeline || !this.lineFill) return;

    const timelineRect = this.timeline.getBoundingClientRect();
    const scrollY = window.pageYOffset;
    const viewportHeight = window.innerHeight;

    // Calculate progress based on timeline position relative to viewport
    const timelineTop = scrollY + timelineRect.top;
    const timelineHeight = this.timeline.offsetHeight;
    const viewportBottom = scrollY + viewportHeight;

    let progress = 0;
    if (viewportBottom > timelineTop) {
      const scrolledPastTop = Math.max(0, viewportBottom - timelineTop);
      const totalScrollDistance = timelineHeight + viewportHeight;
      progress = scrolledPastTop / totalScrollDistance;
    }

    progress = Math.max(0, Math.min(progress, 1));
    this.lineFill.style.height = `${progress * timelineHeight}px`;
  },

  initMilestoneVisibility() {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: "0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("lp-tl-visible");
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Initialize milestones with staggered animation delays
    this.milestones.forEach((milestone, index) => {
      milestone.style.animationDelay = `${0.2 + index * 0.1}s`;
      observer.observe(milestone);
    });
  },

  bindEvents() {
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    window.addEventListener("resize", this.handleScroll, { passive: true });
  },

  destroy() {
    if (this.handleScroll) {
      window.removeEventListener("scroll", this.handleScroll);
      window.removeEventListener("resize", this.handleScroll);
    }
  },
};

/**
 * ==============================================================================
 * TECHNOLOGY SHOWCASE - Tab Switching
 * ==============================================================================
 */

const TechnologyTabs = {
  init() {
    this.tabs = safeSelectAll(".lp-tech-tab");
    this.panels = safeSelectAll(".lp-tech-panel");

    if (!this.tabs.length || !this.panels.length) {
      console.log("Technology tabs: Elements not found");
      return;
    }

    this.bindEvents();
    console.log("Technology tabs initialized");
  },

  switchTab(targetIndex) {
    // Update tab states
    this.tabs.forEach((tab, index) => {
      const isActive = index === targetIndex;
      tab.setAttribute("aria-pressed", isActive.toString());
      tab.classList.toggle("active", isActive);
    });

    // Update panel visibility
    this.panels.forEach((panel, index) => {
      const isActive = index === targetIndex;
      panel.classList.toggle("lp-tech-active", isActive);
      panel.setAttribute("aria-hidden", (!isActive).toString());
    });
  },

  bindEvents() {
    this.tabs.forEach((tab, index) => {
      tab.addEventListener("click", () => this.switchTab(index));

      // Keyboard support
      tab.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          this.switchTab(index);
        }
      });
    });
  },
};

/**
 * ==============================================================================
 * YOUTUBE VIDEO - Lazy Loading
 * ==============================================================================
 */

const YouTubeVideo = {
  init() {
    this.thumbnail = safeSelect(".lp-tl-video-thumbnail");
    this.iframe = safeSelect("#youtube-iframe");

    if (!this.thumbnail || !this.iframe) {
      console.log("YouTube video: Elements not found");
      return;
    }

    this.bindEvents();
    console.log("YouTube video lazy loading initialized");
  },

  loadVideo() {
    if (!this.thumbnail || !this.iframe) return;

    // Hide thumbnail with fade effect
    this.thumbnail.style.opacity = "0";
    setTimeout(() => {
      this.thumbnail.style.display = "none";
    }, 300);

    // Show and load iframe
    this.iframe.style.display = "block";
    this.iframe.src =
      "https://www.youtube.com/embed/dCe1L3M09mU?si=KKcjuS5N8Q54tH2A";

    console.log("YouTube video loaded");
  },

  bindEvents() {
    this.thumbnail.addEventListener("click", () => this.loadVideo());

    // Keyboard support
    this.thumbnail.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.loadVideo();
      }
    });
  },
};

/**
 * ==============================================================================
 * TESTIMONIALS - Carousel
 * ==============================================================================
 */

const TestimonialsCarousel = {
  init() {
    this.section = safeSelect("#lp-test-section");
    this.slides = safeSelectAll(".lp-test-slide", this.section);
    this.indicators = safeSelectAll(".lp-test-indicator", this.section);

    if (!this.section || !this.slides.length || !this.indicators.length) {
      console.log("Testimonials carousel: Elements not found");
      return;
    }

    this.currentSlide = 0;
    this.autoRotateInterval = null;
    this.autoRotateDelay = 5000;

    this.bindEvents();
    this.startAutoRotate();
    console.log("Testimonials carousel initialized");
  },

  showSlide(index) {
    // Update slides
    this.slides.forEach((slide, i) => {
      const isActive = i === index;
      slide.classList.toggle("lp-test-active", isActive);
      slide.setAttribute("aria-hidden", (!isActive).toString());
    });

    // Update indicators
    this.indicators.forEach((indicator, i) => {
      const isActive = i === index;
      indicator.classList.toggle("lp-test-indicator-active", isActive);
      indicator.setAttribute("aria-pressed", isActive.toString());
    });

    this.currentSlide = index;
  },

  nextSlide() {
    const nextIndex = (this.currentSlide + 1) % this.slides.length;
    this.showSlide(nextIndex);
  },

  startAutoRotate() {
    this.stopAutoRotate();
    this.autoRotateInterval = setInterval(
      () => this.nextSlide(),
      this.autoRotateDelay
    );
  },

  stopAutoRotate() {
    if (this.autoRotateInterval) {
      clearInterval(this.autoRotateInterval);
      this.autoRotateInterval = null;
    }
  },

  bindEvents() {
    // Indicator clicks
    this.indicators.forEach((indicator, index) => {
      indicator.addEventListener("click", () => {
        this.showSlide(index);
        this.startAutoRotate(); // Restart auto-rotation
      });

      // Keyboard support
      indicator.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          this.showSlide(index);
          this.startAutoRotate();
        }
      });
    });

    // Pause on hover
    this.section.addEventListener("mouseenter", () => this.stopAutoRotate());
    this.section.addEventListener("mouseleave", () => this.startAutoRotate());

    // Pause on focus (accessibility)
    this.section.addEventListener("focusin", () => this.stopAutoRotate());
    this.section.addEventListener("focusout", () => this.startAutoRotate());
  },

  destroy() {
    this.stopAutoRotate();
  },
};

/**
 * ==============================================================================
 * SCROLL TO SECTION - Navigation Helper
 * ==============================================================================
 */

const ScrollNavigation = {
  init() {
    // Make scrollToNextSection globally available for onclick handlers
    window.scrollToNextSection = this.scrollToNextSection.bind(this);
    console.log("Scroll navigation initialized");
  },

  scrollToNextSection() {
    const heroSection = safeSelect("#lp-hero-section");
    const nextSectionId =
      heroSection?.dataset.nextSectionId || "lp-tl-legacy-timeline";
    const nextSection = safeSelect(`#${nextSectionId}`);

    if (nextSection) {
      nextSection.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  },
};

/**
 * ==============================================================================
 * MAIN INITIALIZATION
 * ==============================================================================
 */

class AnniversaryPage {
  constructor() {
    this.modules = [
      HeroParallax,
      TimelineAnimation,
      TechnologyTabs,
      YouTubeVideo,
      TestimonialsCarousel,
      ScrollNavigation,
    ];
  }

  init() {
    console.log("Initializing 15 Year Anniversary Page...");

    this.modules.forEach((module) => {
      try {
        module.init();
      } catch (error) {
        console.error(
          `Failed to initialize ${module.constructor.name}:`,
          error
        );
      }
    });

    console.log("Anniversary page initialization complete");
  }

  destroy() {
    this.modules.forEach((module) => {
      if (typeof module.destroy === "function") {
        module.destroy();
      }
    });
  }
}

const onStayInTheLoopClick = () => {
  const klaviyoForm = document.querySelector(".klaviyo-form-WRJhsh");
  console.log("Klaviyo form element found:", klaviyoForm);

  if (klaviyoForm && !klaviyoForm.classList.contains("show")) {
    console.log("Adding show class to form");
    klaviyoForm.classList.add("show");
    document.body.style.overflow = "hidden"; // Prevent background scrolling
  }

  // Klaviyo API call
  if (window._klOnsite) {
    window._klOnsite.push(["openForm", "WRJhsh"]);
  }
};

// Make function globally available
window.onStayInTheLoopClick = onStayInTheLoopClick;

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  const anniversaryPage = new AnniversaryPage();
  anniversaryPage.init();

  // Make instance available globally for debugging
  window._klOnsite = window._klOnsite || [];
  window.anniversaryPage = anniversaryPage;

  const noThanksButton = document.querySelector(".kl-private-reset-css-Xuajs1");
  noThanksButton.addEventListener("click", () => {
    const klaviyoForm = document.querySelector(".klaviyo-form-WRJhsh");
    if (klaviyoForm && klaviyoForm.classList.contains("show")) {
      klaviyoForm.classList.remove("show");
    }
  });
});

// Global function for YouTube video (called from HTML onclick)
window.loadYouTubeVideo = () => YouTubeVideo.loadVideo();

// Function to close Klaviyo form
window.closeKlaviyoForm = () => {
  const klaviyoForm = document.querySelector(".klaviyo-form-WRJhsh");
  if (klaviyoForm && klaviyoForm.classList.contains("show")) {
    klaviyoForm.classList.remove("show");
    document.body.style.overflow = ""; // Restore scrolling
  }
};

// Close form when clicking outside
document.addEventListener("click", (event) => {
  const klaviyoForm = document.querySelector(".klaviyo-form-WRJhsh");
  if (
    klaviyoForm &&
    klaviyoForm.classList.contains("show") &&
    event.target === klaviyoForm
  ) {
    closeKlaviyoForm();
  }
});

// Close form with Escape key
document.addEventListener("keydown", (event) => {
  if (event.key === "Escape") {
    const klaviyoForm = document.querySelector(".klaviyo-form-WRJhsh");
    if (klaviyoForm && klaviyoForm.classList.contains("show")) {
      closeKlaviyoForm();
    }
  }
});

const onExploreMoreClick = () => {
  const lpTlLegacyTimeline = document.querySelector("#lp-tl-legacy-timeline");
  lpTlLegacyTimeline.scrollIntoView({ behavior: "smooth" });
};

window.onExploreMoreClick = onExploreMoreClick;
